import UnauthorizedPage from "./components/UnauthorizedPage/index";
import React, { Component } from "react";
import { Spinner } from "react-bootstrap";
import "./App.css";
import Header from "./components/header/header";
import NavBar from "./components/nav/navbar";
import FooterPage from "./components/footer/footer";
import DeviceListing from "./components/deviceListing/listing";
import DeviceOnBoarding from "./components/deviceOnBoarding/onBoarding";
import "react-toastify/dist/ReactToastify.css";
import DeviceTwin from "./components/deviceTwin/deviceTwin";
import { ToastContainer } from "react-toastify";
import RangeSlider from "./components/rangeSlider/rangrSlider";
import PayloadForm from "./components/payloadForm/payloadForm";
import Logs from "./components/Logs/Logs";
import {
  Redirect,
  Route,
  RouteComponentProps,
  RouteProps,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import JSONViewEditPage from "./components/JsonViewEditPage/JsonViewEditPage";
import { RolesProvider, useRoles } from "./context/RolesContext";
import BulkUpload from "./components/bulkUpload/bulkUpload";
import { MsalProvider } from "@azure/msal-react";
import {
  EventType,
  AuthenticationResult,
  PublicClientApplication,
  AccountInfo,
  EventMessage,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest, msalConfig } from "./entraidconfig";

export type AppRole = "admin" | "user" | "editor";

interface ProtectedRouteProps extends RouteProps {
  allowedRoles: AppRole[];
  component?: React.ComponentType<any>;
  render?: (props: RouteComponentProps<any>) => React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  component: Component,
  render,
  ...rest
}) => {
  const { roles } = useRoles();

  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps<any>) =>
        roles.some((r) => allowedRoles.includes(r as AppRole)) ? (
          render ? (
            render(props)
          ) : Component ? (
            <Component {...props} {...rest} />
          ) : null
        ) : (
          <Redirect to="/unauthorized" />
        )
      }
    />
  );
};

const pca: IPublicClientApplication = new PublicClientApplication(msalConfig);

pca.addEventCallback((event: EventMessage) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS ||
    event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
    event.eventType === EventType.SSO_SILENT_SUCCESS
  ) {
    const account = (event.payload as AuthenticationResult)?.account;
    if (account) {
      pca.setActiveAccount(account);
    }
  }

  if (event.eventType === EventType.LOGOUT_SUCCESS) {
    pca.setActiveAccount(null);
  }
});

function parseJwt(token: string): any {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Error decoding JWT", e);
    return {};
  }
}

interface AppState {
  load: boolean;
  isAuthenticated: boolean;
  userName: string;
  userEmail: string;
  userRoles: string[];
  userGroups: string[];
  jwtToken: string | null;
  loginPageStyles: {
    container: React.CSSProperties;
    card: React.CSSProperties;
    heading: React.CSSProperties;
    subHeading: React.CSSProperties;
    loginButton: React.CSSProperties;
    loadingContainer: React.CSSProperties;
  };
}

class App extends Component<{}, AppState> {
  private rolesSetter: ((r: string[]) => void) | null = null;
  private groupsSetter: ((g: string[]) => void) | null = null;
  private refreshInterval: NodeJS.Timeout | null = null;

  constructor(props: {}) {
    super(props);
    this.state = {
      load: true,
      isAuthenticated: false,
      userName: "",
      userEmail: "",
      userRoles: [],
      userGroups: [],
      jwtToken: null,
      loginPageStyles: {
        container: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#808080",
        },
        card: {
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          width: "500px",
        },
        heading: {
          fontSize: "24px",
          color: "#333",
          marginBottom: "20px",
        },
        subHeading: {
          fontSize: "16px",
          color: "#666",
          marginBottom: "30px",
        },
        loginButton: {
          backgroundColor: "#007bff",
          color: "#fff",
          padding: "10px 20px",
          border: "none",
          borderRadius: "4px",
          fontSize: "16px",
          cursor: "pointer",
          transition: "background-color 0.3s",
        },
        loadingContainer: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        },
      },
    };
  }

  async componentDidMount() {
    try {
      await pca.initialize();

      const authResult = await pca.handleRedirectPromise();
      console.log("handleRedirectPromise result:", authResult);

      let account: AccountInfo | null =
        authResult?.account || pca.getActiveAccount();

      if (!account) {
        const allAccounts = pca.getAllAccounts();
        console.log("All accounts:", allAccounts);
        if (allAccounts.length > 0) {
          account = allAccounts[0];
          pca.setActiveAccount(account);
        }
      }

      if (account) {
        console.log("Using account:", account);

        try {
          const tokenResult = await pca.acquireTokenSilent({
            ...loginRequest,
            account,
          });

          console.log("Got token:", tokenResult);
          this.applyClaims(tokenResult.accessToken);

          this.refreshInterval = setInterval(
            () => this.refreshRoles(),
            10 * 60 * 1000
          );
        } catch (silentErr) {
          console.warn("⚠️ Silent token failed, retrying redirect", silentErr);
          await pca.acquireTokenRedirect(loginRequest);
        }
      } else {
        console.log("No account found, forcing login");
        this.setState({ isAuthenticated: false });
      }
    } catch (err) {
      console.error("Redirect handling error", err);
      this.setState({ isAuthenticated: false });
    } finally {
      this.setState({ load: false });
    }
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  applyClaims = (accessToken: string) => {
    const claims = parseJwt(accessToken);
    console.log("Applying claims:", claims);

    const name = claims?.name || "";
    const email = claims?.preferred_username || claims?.upn || "";
    const roles: string[] = Array.isArray(claims?.roles) ? claims.roles : [];
    const groups: string[] = Array.isArray(claims?.groups) ? claims.groups : [];

    this.setState({
      userName: name,
      userEmail: email,
      userRoles: roles,
      userGroups: groups,
      isAuthenticated: true,
      jwtToken: accessToken,
    });

    this.rolesSetter?.(roles);
    this.groupsSetter?.(groups);
  };

  refreshRoles = async () => {
    const account = pca.getActiveAccount();
    if (!account) return;

    try {
      const tokenResult = await pca.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });

      this.applyClaims(tokenResult.accessToken);
    } catch (err) {
      console.error("Error refreshing roles", err);
    }
  };

  login = () => {
    pca
      .loginRedirect(loginRequest)
      .catch((err) => console.error("Login error", err));
  };

  logout = () => {
    try {
      sessionStorage.clear();
      localStorage.clear();

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      console.log("Cleared sessionStorage, localStorage, and cookies");
    } catch (err) {
      console.error("Error clearing storage/cookies", err);
    }

    pca.logoutRedirect().catch((err) => console.error("Logout error", err));
  };

  renderAppRoutes = () => {
    const { userName, userEmail, jwtToken, userRoles, userGroups } = this.state;

    return (
      <>
        <Header {...this.state} logout={this.logout} />
        <NavBar {...this.state} logout={this.logout} />

        <Switch>
          <Route
            path="/range-slider"
            render={(props) => <RangeSlider {...props} {...this.state} />}
          />

          <Route
            path="/device-listing"
            render={(props) => <DeviceListing {...props} {...this.state} />}
          />

          <ProtectedRoute
            path="/device-onboarding"
            allowedRoles={["admin"]}
            render={(props: any) => (
              <DeviceOnBoarding
                {...props}
                userName={userName}
                userEmail={userEmail}
                jwtToken={jwtToken}
                userRoles={userRoles}
              />
            )}
          />

          <ProtectedRoute
            path="/bulk-upload"
            allowedRoles={["admin"]}
            render={(props: any) => (
              <BulkUpload
                {...props}
                userName={userName}
                userEmail={userEmail}
                jwtToken={jwtToken}
                userRoles={userRoles}
              />
            )}
          />

          <ProtectedRoute
            path="/payload-form"
            allowedRoles={["admin", "editor"]}
            render={(props: any) => (
              <PayloadForm
                {...props}
                userName={userName}
                userEmail={userEmail}
                jwtToken={jwtToken}
                userRoles={userRoles}
              />
            )}
          />

          <ProtectedRoute
            path="/json-form"
            allowedRoles={["admin"]}
            render={(props: any) => (
              <JSONViewEditPage
                {...props}
                userName={userName}
                userEmail={userEmail}
                jwtToken={jwtToken}
                userRoles={userRoles}
              />
            )}
          />

          <Route
            path="/device-twin"
            render={(props) => <DeviceTwin {...props} {...this.state} />}
          />

          <Route path="/unauthorized" render={() => <UnauthorizedPage />} />

          <Route
            path="/logs"
            render={(props) => <Logs {...props} {...this.state} />}
          />

          <Route
            exact
            path="/"
            render={() => <Redirect to="/device-listing" />}
          />
        </Switch>
      </>
    );
  };

  render() {
    const { load, isAuthenticated, loginPageStyles } = this.state;

    return (
      <MsalProvider instance={pca}>
        <RolesProvider
          onReady={(setRoles, setGroups) => {
            this.rolesSetter = setRoles;
            this.groupsSetter = setGroups;
          }}
        >
          <div>
            {load ? (
              <div style={loginPageStyles.loadingContainer}>
                <Spinner animation="border" variant="primary" />
                <p>Loading...</p>
              </div>
            ) : !isAuthenticated ? (
              <div style={loginPageStyles.container}>
                <div style={loginPageStyles.card}>
                  <h1 style={loginPageStyles.heading}>Simulator</h1>
                  <p style={loginPageStyles.subHeading}>
                    Please login using your ID to access.
                  </p>
                  <button
                    onClick={this.login}
                    style={loginPageStyles.loginButton}
                  >
                    Login
                  </button>
                </div>
              </div>
            ) : (
              <Router>
                {this.renderAppRoutes()}
                <ToastContainer />
                <FooterPage />
              </Router>
            )}
          </div>
        </RolesProvider>
      </MsalProvider>
    );
  }
}

export default App;
